import { ConstructorResults } from '@theme/constructor-results';
import { query, consumeEarlyBrowse, consumeEarlyCards, extractRankedIds } from '@theme/constructor-client';

/**
 * Constructor-ranked collection page.
 *
 * Everything a shopper sees — ranking, sort, facets, infinite scroll — is
 * `ConstructorResults`. This only says how a collection asks Constructor for
 * its products: a browse query on the collection handle.
 *
 * @extends {ConstructorResults}
 */
class ConstructorBrowse extends ConstructorResults {
  /**
   * @param {import('@theme/constructor-results').ResultsRequest} request
   * @returns {Promise<any>}
   */
  async fetchResults(request) {
    const handle = this.dataset.collectionHandle;
    if (!handle) return null;

    const early = await this.#earlyResponse(request, handle);
    if (early) return early;

    const { data } = await query(this.surface, (client, signal) =>
      client.browse.getBrowseResults('group_id', handle, this.requestParameters(request), { signal })
    );
    return data;
  }

  /**
   * The inline <head> script started this same request before the parser
   * reached the grid. Prefer its answer; it is the identical query with the
   * identical personalization parameters, just seconds earlier.
   *
   * The inline <head> request carries neither sort nor facets, so its answer
   * only matches an unfiltered, unsorted first load. Null whenever it cannot be
   * used, and the caller takes the SDK path unchanged.
   *
   * @param {import('@theme/constructor-results').ResultsRequest} request
   * @param {string} handle
   * @returns {Promise<any>}
   */
  async #earlyResponse({ page, pageSize, sort, filters }, handle) {
    const pristine = page === 1 && !sort && !Object.keys(filters).length;
    const early = pristine ? consumeEarlyBrowse({ handle, minResults: pageSize }) : null;
    if (!early) return null;

    try {
      const data = await early;
      return extractRankedIds(data, pageSize).length ? data : null;
    } catch {
      // Blocked, offline, a bad key, a 4xx — the SDK path is the fallback and
      // behaves exactly as it did before this existed.
      return null;
    }
  }

  /**
   * The inline <head> script fetches the first page's cards straight after its
   * browse request. Only page 1 can match; `consumeEarlyCards` compares URLs.
   * @param {URL} url
   */
  prefetchedCards(url) {
    return this.page === 1 ? consumeEarlyCards(url) : null;
  }

  /**
   * A browse grid reports which filter produced it, so Constructor can tell a
   * collection page apart from a search or a recs pod in reporting.
   * @returns {Record<string, string | number | undefined>}
   */
  beaconExtras() {
    return {
      filterName: 'group_id',
      filterValue: this.dataset.collectionHandle,
      page: 1,
    };
  }

  /** @returns {import('@theme/constructor-tracker').ResultContext} */
  trackingContext() {
    return {
      ...super.trackingContext(),
      filterName: 'group_id',
      filterValue: this.dataset.collectionHandle,
    };
  }
}

if (!customElements.get('constructor-browse-component')) {
  customElements.define('constructor-browse-component', ConstructorBrowse);
}
